import {useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Dialog, DialogContent, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {Skeleton} from '@/components/ui/skeleton'
import {Tag} from '@/components/ui/tag'
import {Textarea} from '@/components/ui/textarea'
import {PaginationBar} from '@/components/ui/pagination-bar'
import {DataTable, type Column} from '@/components/data-table'
import {ListCard, ListEmptyState} from '@/components/section-card'
import {PageHeading} from '@/components/page-heading'
import {NoAccessState} from '@/components/no-access-state'
import {ConfirmDialog} from '@/components/confirm-dialog'
import {useIsSystemUser} from '@/hooks/empresa/organizacion/use-businesses'
import {useTableState} from '@/hooks/common/use-table-state'
import {
  LICENCIA_QUEUE_PAGE_SIZE,
  useLicenciaQueue,
  useRejectLicencia,
  useVerifyLicencia,
  type LicenciaQueueItem,
} from '@/hooks/inci/consola/use-licencia'
import {descartarAviso, publicarAviso} from '@/lib/avisos'
import {translateApiError} from '@/lib/errors'
import {SearchInput} from '@/components/ui/search-input'
import {tope} from '@/lib/campos-texto'

const dateFormat = new Intl.DateTimeFormat('es-CO', {year: 'numeric', month: 'short', day: 'numeric'})

function RejectDialog({target, onClose}: {target: LicenciaQueueItem | null; onClose: () => void}) {
  const {t} = useTranslation()
  const reject = useRejectLicencia()
  const [motivo, setMotivo] = useState('')
  const [error, setError] = useState<string | null>(null)

  function handleOpenChange(open: boolean) {
    if (!open) {
      setMotivo('')
      setError(null)
      reject.reset()
      onClose()
    }
  }

  function submit() {
    const trimmed = motivo.trim()
    if (!trimmed) {
      setError(t('licencia.admin.rejectReasonRequired'))
      return
    }
    if (!target) return
    reject.mutate(
      {userId: target.user_id, motivo: trimmed},
      {onSuccess: () => handleOpenChange(false), onError: (err) => setError(translateApiError(err))}
    )
  }

  return (
    <Dialog open={!!target} onOpenChange={handleOpenChange}>
      <DialogContent data-testid="licencia-reject-dialog">
        <DialogHeader>
          <DialogTitle>
            {t('licencia.admin.rejectTitle', {name: target ? `${target.name} ${target.last_name}` : ''})}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          <label htmlFor="licencia-reject-motivo" className="text-sm font-medium text-foreground">
            {t('licencia.admin.rejectReasonLabel')}
          </label>
          <Textarea
            id="licencia-reject-motivo"
            data-testid="licencia-reject-motivo"
            value={motivo}
            onChange={(e) => {
              setMotivo(e.target.value)
              setError(null)
            }}
            maxLength={tope('NOTA')}
            minRows={4}
          />
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button type="button" data-testid="licencia-reject-submit" disabled={reject.isPending} onClick={submit}>
            {t('licencia.admin.rejectSubmit')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

/** Verificar habilita a certificar el SG-SST de cualquier empresa: nunca debe ser más fácil que
 * rechazar, que ya exige motivo. */
function VerifyDialog({
  target,
  pending,
  onClose,
  onConfirm,
}: {
  target: LicenciaQueueItem | null
  pending: boolean
  onClose: () => void
  onConfirm: () => void
}) {
  const {t} = useTranslation()
  return (
    <ConfirmDialog
      open={!!target}
      onOpenChange={(open) => !open && onClose()}
      title={t('licencia.admin.verifyTitle')}
      description={t('licencia.admin.verifyDescription', {name: target ? `${target.name} ${target.last_name}` : ''})}
      confirmLabel={t('licencia.admin.verifySubmit')}
      cancelLabel={t('common.cancel')}
      pending={pending}
      onConfirm={onConfirm}
    />
  )
}

export function AdminLicenciasPage() {
  const {t} = useTranslation()
  const isSystemUser = useIsSystemUser()
  const table = useTableState('name')
  const {data, isLoading} = useLicenciaQueue({page: table.page, search: table.search}, isSystemUser)
  const verify = useVerifyLicencia()
  const [rejectTarget, setRejectTarget] = useState<LicenciaQueueItem | null>(null)
  const [verifyTarget, setVerifyTarget] = useState<LicenciaQueueItem | null>(null)
  // El fallo es una burbuja de error que no se auto-cierra; su id se guarda para descartarla al
  // reintentar, o mentiría sobre el estado de la cola tras un reintento exitoso.
  const avisoFallidoRef = useRef<number | null>(null)

  if (!isSystemUser) return <NoAccessState />

  const columns: Column<LicenciaQueueItem>[] = [
    {
      key: 'name',
      header: t('licencia.admin.table.name'),
      width: 'w-40',
      cell: (row) => (
        <span className="block truncate font-medium text-foreground">
          {row.name} {row.last_name}
        </span>
      ),
    },
    {
      key: 'cedula',
      header: t('licencia.admin.table.cedula'),
      width: 'w-28',
      cell: (row) => <span className="block truncate font-mono text-xs text-muted-foreground">{row.cedula}</span>,
    },
    {
      key: 'numero',
      header: t('licencia.admin.table.numero'),
      width: 'w-32',
      cell: (row) => <span className="block truncate font-mono text-xs text-foreground">{row.numero}</span>,
    },
    {
      key: 'categoria',
      header: t('licencia.admin.table.categoria'),
      width: 'w-32',
      cell: (row) => <Tag tone="neutral">{t(`licencia.categorias.${row.categoria}`)}</Tag>,
    },
    {
      key: 'uploadedAt',
      header: t('licencia.admin.table.uploadedAt'),
      width: 'w-28',
      cell: (row) => (
        <span className="block truncate font-mono text-xs text-muted-foreground">
          {dateFormat.format(new Date(row.uploaded_at))}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: 'w-52',
      className: 'text-right',
      cell: (row) => (
        <div className="flex flex-wrap items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
          <a
            href={row.download_url}
            target="_blank"
            rel="noreferrer"
            data-testid={`licencia-view-${row.user_id}`}
            className="mr-1 text-xs font-semibold text-primary hover:underline">
            {t('licencia.admin.viewDocument')}
          </a>
          <Button
            size="sm"
            variant="outline"
            data-testid={`licencia-verify-${row.user_id}`}
            onClick={() => setVerifyTarget(row)}>
            {t('licencia.admin.verify')}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="text-destructive hover:text-destructive"
            data-testid={`licencia-reject-${row.user_id}`}
            onClick={() => setRejectTarget(row)}>
            {t('licencia.admin.reject')}
          </Button>
        </div>
      ),
    },
  ]

  return (
    <div className="space-y-5" data-testid="admin-licencias">
      <PageHeading title={t('licencia.admin.title')} />

      {isLoading && !data ? (
        <Skeleton className="h-64 w-full" />
      ) : (
        <ListCard
          toolbar={
            <SearchInput
              value={table.searchInput}
              onChange={table.setSearchInput}
              placeholder={t('licencia.admin.searchPlaceholder')}
            />
          }
          footer={
            !!data?.total && (
              <PaginationBar
                page={table.page}
                pageSize={LICENCIA_QUEUE_PAGE_SIZE}
                total={data.total}
                onPageChange={table.setPage}
              />
            )
          }>
          <DataTable
            columns={columns}
            rows={data?.items ?? []}
            rowKey={(row) => row.user_id}
            emptyState={<ListEmptyState page title={t('licencia.admin.empty')} />}
          />
        </ListCard>
      )}

      <RejectDialog target={rejectTarget} onClose={() => setRejectTarget(null)} />
      <VerifyDialog
        target={verifyTarget}
        pending={verify.isPending}
        onClose={() => setVerifyTarget(null)}
        onConfirm={() => {
          if (!verifyTarget) return
          if (avisoFallidoRef.current !== null) {
            descartarAviso(avisoFallidoRef.current)
            avisoFallidoRef.current = null
          }
          verify.mutate(verifyTarget.user_id, {
            onSuccess: () => setVerifyTarget(null),
            onError: (err) => {
              avisoFallidoRef.current = publicarAviso(t('licencia.admin.accionFallida'), translateApiError(err), {
                testId: 'licencia-accion-error',
              })
              setVerifyTarget(null)
            },
          })
        }}
      />
    </div>
  )
}
